// CTV-445: shared helpers for the report's global date-range filter. The range
// is { from, to } as YYYY-MM-DD (or null = unbounded → the full flight window).

export interface ReportRange {
  from: string | null;
  to: string | null;
}

// Build the `?from=&to=` query string for the report endpoints; empty when both
// bounds are null (let the API use the full flight window).
export function rangeQuery(range: ReportRange): string {
  const p = new URLSearchParams();
  if (range.from) p.set('from', range.from);
  if (range.to) p.set('to', range.to);
  const s = p.toString();
  return s ? `?${s}` : '';
}

const pad = (n: number) => String(n).padStart(2, '0');

export function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function addDaysISO(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

// ISO date strings sort lexicographically, so min/max are plain comparisons.
export const minISO = (a: string, b: string): string => (a < b ? a : b);
export const maxISO = (a: string, b: string): string => (a > b ? a : b);

// The filter presets, shared by the filter UI and the section badges so their
// labels never drift. Presets anchor to the last delivered day (campaign end,
// capped at today) and clamp the start to the flight window.
export interface RangePreset {
  key: string;
  label: string;
  range: ReportRange;
}
export function rangePresets(start: string, end: string): RangePreset[] {
  const anchorEnd = minISO(end, todayISO());
  const lastN = (n: number): ReportRange => ({ from: maxISO(start, addDaysISO(anchorEnd, -(n - 1))), to: anchorEnd });
  return [
    { key: 'all', label: 'All', range: { from: null, to: null } },
    { key: '7', label: 'Last 7 days', range: lastN(7) },
    { key: '30', label: 'Last 30 days', range: lastN(30) },
  ];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDay = (iso: string): string => {
  const [, m, d] = iso.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}`;
};

// A short human label for the active range, shown next to section titles.
// Returns null for the full (unfiltered) window, a preset name when one matches,
// else a "Apr 10 – Apr 20" date span.
export function rangeLabel(range: ReportRange, start: string, end: string): string | null {
  if (!range.from && !range.to) return null;
  const preset = rangePresets(start, end).find(
    (p) => p.key !== 'all' && p.range.from === range.from && p.range.to === range.to,
  );
  if (preset) return preset.label;
  return `${fmtDay(range.from ?? start)} – ${fmtDay(range.to ?? end)}`;
}
